<template>
  <section class="reviews-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">What Our Customers Say</h2>
        <p class="section-subtitle">Real experiences from travelers who explored Rwanda with us</p>
        <div class="overall-rating">
          <div class="rating-stars">
            <span v-for="star in 5" :key="star" class="star filled">★</span>
          </div>
          <span class="rating-text">4.9/5 based on 127 reviews</span>
        </div>
      </div>

      <div class="reviews-grid">
        <div v-for="review in reviews" :key="review.id" class="review-card">
          <div class="review-header">
            <div class="reviewer-info">
              <div v-if="review.avatar" class="reviewer-avatar-wrapper">
                <img :src="review.avatar" :alt="review.name" class="reviewer-avatar">
              </div>
              <div v-else class="reviewer-avatar-initials" :style="getAvatarStyle(review.initials)">
                {{ review.initials }}
              </div>
              <div class="reviewer-details">
                <h4 class="reviewer-name">{{ review.name }}</h4>
                <span class="reviewer-location">{{ review.location }}</span>
                <div class="review-verified" v-if="review.verified">
                  <i class="fas fa-check-circle"></i>
                  <span>Verified Customer</span>
                </div>
              </div>
            </div>
            <div class="review-rating">
              <div class="rating-stars">
                <span v-for="star in 5" :key="star"
                      :class="['star', { 'filled': star <= review.rating }]">★</span>
              </div>
              <span class="rating-number">{{ review.rating }}/5</span>
            </div>
          </div>

          <div class="review-content">
            <p class="review-text">"{{ review.text }}"</p>
            <div class="review-trip" v-if="review.tripDetails">
              <i class="fas fa-car"></i>
              <span>{{ review.tripDetails.vehicle }} • {{ review.tripDetails.destination }}</span>
            </div>
          </div>

          <div class="review-footer">
            <div class="review-meta">
              <span class="review-date">{{ formatDate(review.date) }}</span>
              <span class="review-source">{{ review.source }}</span>
            </div>
            <div class="review-actions">
              <button class="helpful-btn" @click="markHelpful(review.id)">
                <i class="fas fa-thumbs-up"></i>
                <span>{{ review.helpful || 0 }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="reviews-cta">
        <router-link to="/customer-photos" class="view-photos-btn">
          <i class="fas fa-camera"></i>
          View Customer Photos
        </router-link>
        <button class="write-review-btn" @click="scrollToContact">
          <i class="fas fa-pen"></i>
          Write a Review
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ReviewsSection',
  data () {
    return {
      reviews: [
        {
          id: 1,
          name: 'Sarah Johnson',
          location: 'New York, USA',
          rating: 5,
          text: 'Amazing experience! The Land Cruiser was perfect for our gorilla trekking adventure. The vehicle was spotless, comfortable, and handled the mountain roads beautifully. The team was incredibly professional and even provided us with a local guide contact. Highly recommend!',
          date: '2024-01-15',
          avatar: null,
          initials: 'SJ',
          verified: true,
          source: 'Google',
          tripDetails: {
            vehicle: 'Toyota Land Cruiser',
            destination: 'Volcanoes National Park'
          },
          helpful: 12
        },
        {
          id: 2,
          name: 'Michael Chen',
          location: 'London, UK',
          rating: 5,
          text: 'Excellent car rental service in Kigali. The Mercedes GLE was luxurious and perfect for our family safari to Akagera. The booking process was seamless, and the staff went above and beyond to ensure we had everything we needed. The GPS was pre-loaded with all the major destinations.',
          date: '2024-01-10',
          avatar: null,
          initials: 'MC',
          verified: true,
          source: 'TripAdvisor',
          tripDetails: {
            vehicle: 'Mercedes GLE',
            destination: 'Akagera National Park'
          },
          helpful: 8
        },
        {
          id: 3,
          name: 'Emma Wilson',
          location: 'Melbourne, Australia',
          rating: 5,
          text: 'Outstanding service! We rented the Range Rover Sport for our 10-day Rwanda adventure. The car was immaculate and performed flawlessly on all terrains. The team provided excellent customer service and even helped us plan our itinerary. Worth every penny!',
          date: '2024-01-08',
          avatar: null,
          initials: 'EW',
          verified: true,
          source: 'Google',
          tripDetails: {
            vehicle: 'Range Rover Sport',
            destination: 'Multi-destination tour'
          },
          helpful: 15
        },
        {
          id: 4,
          name: 'David Brown',
          location: 'Toronto, Canada',
          rating: 5,
          text: 'Perfect for our chimpanzee trekking in Nyungwe Forest! The Toyota RAV4 was comfortable and reliable. The staff was knowledgeable about the best routes and even provided us with a detailed map. The car was clean, well-maintained, and had excellent fuel efficiency.',
          date: '2024-01-05',
          avatar: null,
          initials: 'DB',
          verified: true,
          source: 'Facebook',
          tripDetails: {
            vehicle: 'Toyota RAV4',
            destination: 'Nyungwe Forest'
          },
          helpful: 6
        },
        {
          id: 5,
          name: 'Lisa Anderson',
          location: 'Stockholm, Sweden',
          rating: 5,
          text: 'Fantastic experience from start to finish! The Lexus RX was perfect for our Lake Kivu getaway. The booking was easy, pickup was smooth, and the car was in excellent condition. The team even provided us with local restaurant recommendations. Highly professional service!',
          date: '2024-01-03',
          avatar: null,
          initials: 'LA',
          verified: true,
          source: 'Google',
          tripDetails: {
            vehicle: 'Lexus RX',
            destination: 'Lake Kivu'
          },
          helpful: 9
        },
        {
          id: 6,
          name: 'James Taylor',
          location: 'Cape Town, South Africa',
          rating: 5,
          text: 'Outstanding service and vehicle quality! The Nissan X-Trail was perfect for our family trip to Musanze Caves. The car was spacious, comfortable, and handled the mountain roads excellently. The team was very helpful with local tips and recommendations.',
          date: '2024-01-01',
          avatar: null,
          initials: 'JT',
          verified: true,
          source: 'TripAdvisor',
          tripDetails: {
            vehicle: 'Nissan X-Trail',
            destination: 'Musanze Caves'
          },
          helpful: 7
        }
      ]
    }
  },
  methods: {
    formatDate (dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    markHelpful (reviewId) {
      const review = this.reviews.find(r => r.id === reviewId)
      if (review) {
        review.helpful = (review.helpful || 0) + 1
      }
    },
    scrollToContact () {
      this.$router.push('/contact')
    },
    getAvatarStyle (initials) {
      const colors = [
        { bg: '#FF6B35', text: '#FFFFFF' },
        { bg: '#002F6C', text: '#FFFFFF' },
        { bg: '#FF8C42', text: '#FFFFFF' },
        { bg: '#4A90E2', text: '#FFFFFF' },
        { bg: '#50C878', text: '#FFFFFF' },
        { bg: '#9B59B6', text: '#FFFFFF' }
      ]
      const index = (initials.charCodeAt(0) + initials.charCodeAt(1)) % colors.length
      return {
        backgroundColor: colors[index].bg,
        color: colors[index].text
      }
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.reviews-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #002F6C;
  margin-bottom: 15px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 30px;
}

.overall-rating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.rating-stars {
  display: flex;
  gap: 2px;
}

.star {
  font-size: 1.2rem;
  color: #ddd;
  transition: color 0.3s ease;
}

.star.filled {
  color: #FF8C42;
}

.rating-text {
  font-weight: 600;
  color: #002F6C;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
}

.review-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.review-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.reviewer-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.reviewer-avatar-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #FF8C42;
}

.reviewer-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reviewer-avatar-initials {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  border: 3px solid #FF8C42;
}

.reviewer-details {
  flex: 1;
}

.reviewer-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #002F6C;
  margin: 0 0 5px 0;
}

.reviewer-location {
  font-size: 0.9rem;
  color: #6c757d;
  display: block;
  margin-bottom: 5px;
}

.review-verified {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #28a745;
}

.review-verified i {
  font-size: 0.9rem;
}

.review-rating {
  text-align: right;
}

.rating-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: #002F6C;
  margin-top: 5px;
  display: block;
}

.review-content {
  margin-bottom: 20px;
}

.review-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 15px;
  font-style: italic;
}

.review-trip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 8px;
}

.review-trip i {
  color: #FF8C42;
}

.review-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.review-meta {
  display: flex;
  gap: 15px;
  font-size: 0.9rem;
  color: #6c757d;
}

.review-source {
  background: #FF8C42;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.helpful-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid #e9ecef;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
}

.helpful-btn:hover {
  background: #FF8C42;
  color: white;
  border-color: #FF8C42;
}

.reviews-cta {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.view-photos-btn,
.write-review-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-size: 1rem;
}

.view-photos-btn {
  background: #002F6C;
  color: white;
}

.view-photos-btn:hover {
  background: #001F47;
  transform: translateY(-2px);
}

.write-review-btn {
  background: #FF8C42;
  color: white;
}

.write-review-btn:hover {
  background: #E67E22;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .review-card {
    padding: 20px;
  }

  .section-title {
    font-size: 2rem;
  }

  .reviews-cta {
    flex-direction: column;
    align-items: center;
  }

  .view-photos-btn,
  .write-review-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}
</style>
